// deps
import React, { createContext, useContext, ReactElement, ReactNode } from "react";

// internal deps
import { BillImageRequiredHalfCard } from "@swiggy-private/payment-ui";

// hooks
import { useBillImageHalfCard } from "@payment-client/hooks/UseBillImageHalfCard";

// interfaces
import { OpenBillImageHalfCardParams } from "@payment-client/hooks/UseBillImageHalfCard";

interface BillImageHalfCardContextValue {
    show: boolean;
    open: (params: OpenBillImageHalfCardParams) => void;
    close: VoidFunction;
    onProceed: VoidFunction;
    onClose: VoidFunction;
    onDeny: VoidFunction;
    analyticsEvents: {
        billUploadHalfCardImpressionEvent: (context?: string) => void;
        billUploadHalfCardProceedClickEvent: (context?: string) => void;
        billUploadHalfCardDenyClickEvent: VoidFunction;
        billUploadHalfCardCloseClickEvent: VoidFunction;
    };
}

const BillImageHalfCardContext = createContext<BillImageHalfCardContextValue | null>(null);

interface BillImageHalfCardProviderProps {
    children: ReactNode;
}

export const BillImageHalfCardProvider = ({ children }: BillImageHalfCardProviderProps): ReactElement => {
    const { show, open, close, onProceed, onClose, onDeny, analyticsEvents } = useBillImageHalfCard();

    const contextValue: BillImageHalfCardContextValue = {
        show,
        open,
        close,
        onProceed,
        onClose,
        onDeny,
        analyticsEvents,
    };

    return (
        <BillImageHalfCardContext.Provider value={contextValue}>
            {children}
            {/* Bill Image Required Half Card */}
            {show && <BillImageRequiredHalfCard onProceed={onProceed} onClose={onClose} onDeny={onDeny} />}
        </BillImageHalfCardContext.Provider>
    );
};

export const useBillImageHalfCardContext = (): BillImageHalfCardContextValue => {
    const context = useContext(BillImageHalfCardContext);
    if (!context) {
        throw new Error("useBillImageHalfCardContext must be used within a BillImageHalfCardProvider");
    }
    return context;
};
